import { useEffect, useRef, useState, useMemo } from 'react';
import { useRichText } from '../hooks/useRichText';
import marked, { Renderer } from 'marked';
import escapeHtml from 'escape-html';
import insane from '@vtex/insane';
import { TextAlignmentEnum, TextPositionEnum } from '../@types/RichTextProps';

const getTargetFromUrl = (url: string) => {
	const urlSplit = url.split('?');

	if (urlSplit.length < 2) {
		return '';
	}

	const [, qs] = urlSplit;
	const hastTargetBlank = qs.includes('target=_blank');

	return hastTargetBlank ? 'target=_blank' : '';
};

const sanitizerConfig = {
	allowedTags: [
		'a',
		'br',
		'div',
		'h1',
		'h2',
		'h3',
		'h4',
		'h5',
		'h6',
		'iframe',
		'img',
		'li',
		'ol',
		'p',
		'span',
		'sup',
		'table',
		'tbody',
		'td',
		'th',
		'thead',
		'tr',
		'ul',
	],
	allowedAttributes: {
		'*': ['class', 'title'],
		a: ['href', 'target'],
		img: ['src', 'alt'],
		iframe: ['frameborder', 'height', 'src', 'width', 'style'],
	},
	allowedSchemes: ['http', 'https', 'mailto', 'tel'],
};

const getPosition = {
	LEFT: 'items-start justify-start',
	RIGHT: 'items-end justify-end',
	CENTER: 'items-center justify-center',
};

const getAlignment = {
	LEFT: 'tl',
	RIGHT: 'tr',
	CENTER: 'tc',
};

const defaultClass = 'vtex-rich-text-0-x-';

const getLevel = (level: number) => (level > 0 && level <= 6 ? level : 6);

const getHeadingLevelClass = (blockClass: string, level: number) => {
	if (level === 1)
		return `${defaultClass}headingLevel1 headingLevel1 ${defaultClass}headingLevel1--${blockClass} headingLevel1--${blockClass} ${defaultClass}heading-level-1 heading-level-1`;
	if (level === 2)
		return `${defaultClass}headingLevel2 headingLevel2 ${defaultClass}headingLevel2--${blockClass} headingLevel2--${blockClass} ${defaultClass}heading-level-2 heading-level-2`;
	if (level === 3)
		return `${defaultClass}headingLevel3 headingLevel3 ${defaultClass}headingLevel3--${blockClass} headingLevel3--${blockClass} ${defaultClass}heading-level-3 heading-level-3`;
	if (level === 4)
		return `${defaultClass}headingLevel4 headingLevel4 ${defaultClass}headingLevel4--${blockClass} headingLevel4--${blockClass} ${defaultClass}heading-level-4 heading-level-4`;
	if (level === 5)
		return `${defaultClass}headingLevel5 headingLevel5 ${defaultClass}headingLevel5--${blockClass} headingLevel5--${blockClass} ${defaultClass}heading-level-5 heading-level-5`;
	if (level === 6)
		return `${defaultClass}headingLevel6 headingLevel6 ${defaultClass}headingLevel6--${blockClass} headingLevel6--${blockClass} ${defaultClass}heading-level-6 heading-level-6`;

	return '';
};

const RichTextContainer = () => {
	const [isMounted, setMounted] = useState(false);
	const renderer = useRef<Renderer>();
	const {
		blockClass = '',
		text = '',
		textColor = 'c-on-base',
		font = 't-body',
		textAlignment = 'LEFT',
		textPosition = 'LEFT',
		componentId,
	} = useRichText();

	const renderHeading = () => (text: string, level: number) => {
		const levelNumber = getLevel(level);
		const classes = `${defaultClass}heading heading ${defaultClass}heading--${blockClass} heading--${blockClass} t-heading-${levelNumber} ${getHeadingLevelClass(blockClass, levelNumber)}`;

		return `<h${levelNumber} class="${classes}">${text}</h${levelNumber}>`;
	};

	useEffect(() => {
		setMounted(true);
	}, []);

	if (!isMounted) {
		renderer.current = new Renderer();
		renderer.current.paragraph = (content: string) =>
			`<p class="lh-copy ${defaultClass}paragraph paragraph ${defaultClass}paragraph--${blockClass} paragraph--${blockClass}">${content}</p>`;
		renderer.current.strong = (content: string) =>
			`<span class="b ${defaultClass}strong strong ${defaultClass}strong--${blockClass} strong--${blockClass}">${content}</span>`;
		renderer.current.em = (content: string) =>
			`<span class="i ${defaultClass}italic italic ${defaultClass}italic--${blockClass} italic--${blockClass}">${content}</span>`;
		renderer.current.heading = renderHeading();
		renderer.current.link = (href: string, title: string, content: string) => {
			const targetAtr = getTargetFromUrl(href);
			const targetRemoved = targetAtr ? href.replace(/target=_blank/, '').replace(/\?&/, '?') : href;

			// clean trailing ? or &
			const cleanHref = targetRemoved.replace(/(\?|&)$/, '');
			const titleAtr = title ? `title="${title}"` : '';

			let finalLink = `<a class="${defaultClass}link link ${defaultClass}link--${blockClass} link--${blockClass}" href="${cleanHref}"`;

			if (titleAtr) {
				finalLink += ` ${titleAtr}`;
			}

			if (targetAtr) {
				finalLink += ` ${targetAtr}`;
			}

			finalLink += `>${content}</a>`;

			return finalLink;
		};

		renderer.current.html = (html) => escapeHtml(html);

		renderer.current.image = (href: string, title: string, content: string) =>
			`<img class="${defaultClass}image image ${defaultClass}image--${blockClass} image--${blockClass}" src="${href}" alt="${content}" ${title ? `title="${title}"` : ''} />`;

		renderer.current.list = (body: string, ordered: boolean) => {
			const tag = ordered ? 'ol' : 'ul';

			return `<${tag} class="${defaultClass}list list ${defaultClass}list--${blockClass} list--${blockClass} ${ordered ? `${defaultClass}listOrdered listOrdered ${defaultClass}listOrdered--${blockClass} listOrdered--${blockClass}` : ''}">${body}</${tag}>`;
		};

		renderer.current.listitem = (content: string) =>
			`<li class="${defaultClass}listItem listItem ${defaultClass}listItem--${blockClass} listItem--${blockClass} ">${content}</li>`;
	}

	//GROOMING
	const renderTokens = () => {
		return `${textColor} ${font} ${getAlignment[textAlignment.toUpperCase() as TextAlignmentEnum]} ${getPosition[textPosition.toUpperCase() as TextPositionEnum]}`;
	};

	const html = useMemo(() => {
		marked.setOptions({
			gfm: true,
			breaks: true,
			sanitize: false, // Use insane lib for sanitizing
			smartLists: true,
			renderer: renderer.current,
		});

		return insane(
			// TODO: While markdown component isn't released, it needs to be done this way.
			marked(String(text!).replace(/\\n/gi, `<br>`)),
			sanitizerConfig,
		);
	}, [text]);
	return (
		// <div>
		<div
			data-selection={componentId}
			className={`vtex-rich-text-0-x-container container vtex-rich-text-0-x-container--${blockClass} container--${blockClass} flex ${renderTokens()}`}
		>
			<div
				className={`vtex-rich-text-0-x-wrapper wrapper vtex-rich-text-0-x-wrapper--${blockClass} wrapper--${blockClass}`}
				dangerouslySetInnerHTML={{ __html: html }}
			/>
		</div>
		// </div>
	);
};

export default RichTextContainer;
